import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useSession } from '../lib/session';
import type { LeadershipExceptions, MyWork } from '../lib/types';
import { formatDate, overdueLabel } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Loading,
  PageHeader,
  SectionNote,
  StatTile,
} from '../components/ui';

/**
 * Home.
 *
 * §69 defines success as three questions, one per audience. Home asks the one
 * that belongs to whoever is looking: a CSM sees their day, leadership sees where
 * execution is slipping. Nobody gets a landing page they have to interpret.
 */
export function HomePage() {
  const { me } = useSession();
  const isLeadership = me.capabilities.canSeeAllAccounts;

  const myWork = useQuery({
    queryKey: ['my-work', 'mine'],
    queryFn: () => api.get<MyWork>('/cs/my-work', { scope: 'mine' }),
  });

  const leadership = useQuery({
    queryKey: ['leadership'],
    queryFn: () => api.get<LeadershipExceptions>('/cs/leadership/exceptions'),
    enabled: isLeadership,
  });

  const topItems = (myWork.data?.items ?? []).slice(0, 6);

  return (
    <>
      <PageHeader
        title={`Good day, ${me.fullName.split(' ')[0]}`}
        description={
          isLeadership
            ? 'Where we are failing to do what we said we would do, and what is on your own plate.'
            : 'What must you do today so that nothing you promised gets forgotten?'
        }
      />

      {(myWork.isLoading || (isLeadership && leadership.isLoading)) && <Loading />}

      <div className="space-y-6">
        {myWork.data && (
          <Card padded={false}>
            <div className="px-4 pt-4">
              <CardHeader
                title="Your next actions"
                count={myWork.data.total}
                description="The top of your list, in §21 priority order."
                actions={
                  <Link to="/my-work">
                    <Button size="sm">Open My Work</Button>
                  </Link>
                }
              />
            </div>
            {topItems.length === 0 ? (
              <div className="px-4 pb-4">
                <EmptyState
                  title="Nothing needs you today"
                  description="No overdue commitments, no follow-ups due, no readiness checks outstanding."
                />
              </div>
            ) : (
              <ul className="divide-y divide-border border-t border-border">
                {topItems.map((item) => (
                  <li
                    key={`${item.kind}-${item.workItemId}`}
                    className="flex items-start justify-between gap-3 px-4 py-2.5"
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-ink">{item.title}</p>
                      <p className="mt-0.5 text-xs text-ink-2">
                        <Link
                          to={`/accounts/${item.accountId}`}
                          className="text-accent hover:underline"
                        >
                          {item.accountName}
                        </Link>
                        {' · '}
                        {item.label}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <Badge tone={(item.businessDaysOverdue ?? 0) > 0 ? 'critical' : 'neutral'}>
                        {overdueLabel(item.businessDaysOverdue)}
                      </Badge>
                      <p className="mt-0.5 text-xs text-muted">{formatDate(item.dueAt)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {isLeadership && leadership.data && (
          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-semibold tracking-tight text-ink">
                Across all accounts
              </h2>
              <Link to="/leadership" className="text-xs text-accent hover:underline">
                Open the exception dashboard
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              <StatTile
                label="Overdue commitments"
                value={leadership.data.counts.overdueCommitments}
                tone="critical"
              />
              <StatTile
                label="Escalated"
                value={leadership.data.counts.escalated}
                tone="critical"
              />
              <StatTile
                label="At risk"
                value={leadership.data.counts.commitmentsAtRisk}
                tone="serious"
              />
              <StatTile
                label="Overdue follow-ups"
                value={leadership.data.counts.overdueFollowups}
                tone="serious"
              />
              <StatTile
                label="Behind cadence"
                value={leadership.data.counts.accountsBehindCadence}
                tone="warning"
                context={`${leadership.data.counts.strategicAccountsBehindCadence} strategic`}
              />
              <StatTile
                label="Customers to notify"
                value={leadership.data.counts.pendingNotifications}
                tone="info"
              />
            </div>
          </section>
        )}

        <Card>
          <CardHeader
            title="What this platform is for"
            description="The operating principle behind every screen here."
          />
          <blockquote className="border-l-2 border-accent pl-3 text-sm leading-relaxed text-ink">
            Nothing promised to a customer should be forgotten, and no important customer should be
            neglected because someone forgot what needed to happen next.
          </blockquote>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <p className="text-xs font-semibold text-ink">Built and working</p>
              <ul className="mt-1 space-y-0.5 text-xs text-ink-2">
                <li>Enterprise and account hierarchy, with ownership and temporary coverage</li>
                <li>Success plans, projects and deliverables</li>
                <li>Commitments with separate customer, internal and readiness dates</li>
                <li>Dependencies, follow-ups and opportunity signals</li>
                <li>Cadence engine, escalation, leadership exceptions</li>
                <li>Full audit trail and the event outbox</li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-ink">Next</p>
              <ul className="mt-1 space-y-0.5 text-xs text-ink-2">
                <li>Sources and AI proposals, with accept / edit / reject (Phase 3)</li>
                <li>Email and calendar ingestion, response-required detection (Phase 4)</li>
                <li>Fireflies transcripts and customer signals (Phase 5)</li>
                <li>Health recommendation and the Weekly CS Review (Phase 6)</li>
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-ink">Then</p>
              <ul className="mt-1 space-y-0.5 text-xs text-ink-2">
                <li>Product inbox, product items, duplicate detection (Phase 7)</li>
                <li>Roadmap and promise governance against real product dates (Phase 8)</li>
                <li>GitLab linkage, estimates and actuals (Phase 9)</li>
                <li>Releases, customer notifications, biweekly updates (Phase 10)</li>
              </ul>
            </div>
          </div>
        </Card>

        <SectionNote>
          Every action you take here is recorded against you in the audit trail, and nothing the
          platform automates can send a customer email, make a commitment, or close a follow-up on
          your behalf.
        </SectionNote>
      </div>
    </>
  );
}
